import { useState, useEffect } from "react";
import { Calculator, DollarSign, TrendingUp, Users, Clock, Plus, Euro } from "lucide-react";
import { PaymentComponent } from "./PaymentComponent";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MultiSelectProducts } from "@/components/invoices/MultiSelectProducts";

// Sub-fields for detailed cost breakdown
interface CostBreakdown {
  design: number;
  moderation_analysis: number;
  panel_incentives: number;
  reports: number;
  other: number;
}

interface ToolCosts {
  external_costs: number;
  internal_costs: number;
  external_breakdown?: CostBreakdown;
  internal_breakdown?: CostBreakdown;
}

interface MultiToolCosts {
  toolCosts: { [tool: string]: ToolCosts };
  general_external_costs: number;
  general_internal_costs: number;
  notes: string;
}

interface SingleToolCosts {
  external_costs: number;
  internal_costs: number;
  external_breakdown?: CostBreakdown;
  internal_breakdown?: CostBreakdown;
  notes: string;
}

interface ProjectCostTabProps {
  project: any;
  isEditingInline: boolean;
  onEditingChange: (editing: boolean) => void;
}

// Cost breakdown sub-field labels
const COST_BREAKDOWN_FIELDS: { key: keyof CostBreakdown; label: string }[] = [
  { key: 'design', label: 'Design' },
  { key: 'moderation_analysis', label: 'Moderation Analysis' },
  { key: 'panel_incentives', label: 'Panel/Incentives' },
  { key: 'reports', label: 'Reports' },
  { key: 'other', label: 'Other' },
];

const getDefaultBreakdown = (): CostBreakdown => ({
  design: 0,
  moderation_analysis: 0,
  panel_incentives: 0,
  reports: 0,
  other: 0,
});

const calculateBreakdownTotal = (breakdown?: CostBreakdown): number => {
  if (!breakdown) return 0;
  return (
    (parseFloat(String(breakdown.design)) || 0) +
    (parseFloat(String(breakdown.moderation_analysis)) || 0) +
    (parseFloat(String(breakdown.panel_incentives)) || 0) +
    (parseFloat(String(breakdown.reports)) || 0) +
    (parseFloat(String(breakdown.other)) || 0)
  );
};

// Cost Component (reusable for both Initial and Actual)
function CostComponent({
  title,
  icon,
  costs,
  setCosts,
  tools,
  products = [],
  hasMultipleTools,
  isEditingInline,
  isActual = false
}: {
  title: string;
  icon: React.ReactNode;
  costs: MultiToolCosts | SingleToolCosts;
  setCosts: (costs: MultiToolCosts | SingleToolCosts) => void;
  tools: string[];
  products?: any[];
  hasMultipleTools: boolean;
  isEditingInline: boolean;
  isActual?: boolean;
}) {
  const calculateTotal = (costs: MultiToolCosts | SingleToolCosts): number => {
    if (hasMultipleTools && 'toolCosts' in costs) {
      const toolsTotal = Object.values(costs.toolCosts).reduce((sum, tool) => {
        const externalTotal = calculateBreakdownTotal(tool.external_breakdown);
        const internalTotal = calculateBreakdownTotal(tool.internal_breakdown);
        return sum + externalTotal + internalTotal;
      }, 0);
      return toolsTotal;
    } else if (!hasMultipleTools && 'external_breakdown' in costs) {
      const externalTotal = calculateBreakdownTotal(costs.external_breakdown);
      const internalTotal = calculateBreakdownTotal(costs.internal_breakdown);
      return externalTotal + internalTotal;
    }
    return 0;
  };

  const updateToolBreakdown = (tool: string, type: 'external_breakdown' | 'internal_breakdown', field: keyof CostBreakdown, value: string) => {
    if (hasMultipleTools) {
      const current = costs as MultiToolCosts;
      const currentBreakdown = current.toolCosts[tool]?.[type] || getDefaultBreakdown();
      
      setCosts({
        ...current,
        toolCosts: {
          ...current.toolCosts,
          [tool]: {
            ...current.toolCosts[tool],
            [type]: {
              ...currentBreakdown,
              [field]: parseFloat(value) || 0
            }
          }
        }
      } as MultiToolCosts);
    }
  };

  const updateSingleBreakdown = (type: 'external_breakdown' | 'internal_breakdown', field: keyof CostBreakdown, value: string) => {
    const current = costs as SingleToolCosts;
    const currentBreakdown = current[type] || getDefaultBreakdown();
    
    setCosts({
      ...current,
      [type]: {
        ...currentBreakdown,
        [field]: parseFloat(value) || 0
      }
    } as SingleToolCosts);
  };

  const getToolName = (toolId: string) => {
    const product = products.find(p => p.id === toolId);
    return product?.name || toolId;
  };

  const total = calculateTotal(costs);

  const renderBreakdownFields = (
    breakdown: CostBreakdown | undefined,
    type: 'external_breakdown' | 'internal_breakdown',
    toolId?: string
  ) => {
    const currentBreakdown = breakdown || getDefaultBreakdown();
    const prefix = isActual ? 'actual_' : '';
    const idPrefix = toolId ? `${prefix}tool_${toolId}_${type}` : `${prefix}${type}`;

    return (
      <div className="pl-4 space-y-2 border-l-2 border-muted">
        {COST_BREAKDOWN_FIELDS.map(({ key, label }) => (
          <div key={key} className="flex items-center justify-between gap-2">
            <Label htmlFor={`${idPrefix}_${key}`} className="text-sm text-muted-foreground flex-shrink-0 min-w-[140px]">
              {label}
            </Label>
            {isEditingInline ? (
              <Input
                id={`${idPrefix}_${key}`}
                type="number"
                value={currentBreakdown[key] === 0 ? '' : currentBreakdown[key]}
                onChange={(e) => {
                  if (toolId) {
                    updateToolBreakdown(toolId, type, key, e.target.value);
                  } else {
                    updateSingleBreakdown(type, key, e.target.value);
                  }
                }}
                placeholder="0"
                className="w-28 h-8 text-sm"
              />
            ) : (
              <span className="text-sm font-medium">
                €{(currentBreakdown[key] || 0).toLocaleString()}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasMultipleTools && 'toolCosts' in costs ? (
          <>
            {/* Per-tool breakdown */}
            {tools.map((tool: string) => {
              const toolCost = costs.toolCosts[tool] || { external_breakdown: getDefaultBreakdown(), internal_breakdown: getDefaultBreakdown() };
              const externalTotal = calculateBreakdownTotal(toolCost.external_breakdown);
              const internalTotal = calculateBreakdownTotal(toolCost.internal_breakdown);
              
              return (
                <div key={tool} className="p-4 border rounded-lg bg-muted/50">
                  <h4 className="font-medium mb-3 flex items-center">
                    <Plus className="h-4 w-4 mr-2" />
                    {getToolName(tool)}
                  </h4>
                  <div className="space-y-4">
                    {/* External Costs */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label className="font-medium">External Costs</Label>
                        <span className="text-sm font-semibold">€{externalTotal.toLocaleString()}</span>
                      </div>
                      {renderBreakdownFields(toolCost.external_breakdown, 'external_breakdown', tool)}
                    </div>
                    
                    {/* Internal Costs */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label className="font-medium">Internal Costs</Label>
                        <span className="text-sm font-semibold">€{internalTotal.toLocaleString()}</span>
                      </div>
                      {renderBreakdownFields(toolCost.internal_breakdown, 'internal_breakdown', tool)}
                    </div>
                    
                    <div className="pt-2 border-t">
                      <div className="flex justify-between items-center text-sm">
                        <span>Tool Total:</span>
                        <span className="font-semibold">
                          €{(externalTotal + internalTotal).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <>
            {/* Single tool or no tools */}
            {(() => {
              const singleCosts = costs as SingleToolCosts;
              const externalTotal = calculateBreakdownTotal(singleCosts.external_breakdown);
              const internalTotal = calculateBreakdownTotal(singleCosts.internal_breakdown);
              
              return (
                <>
                  {/* External Costs */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="font-medium">External Costs</Label>
                      <span className="text-sm font-semibold">€{externalTotal.toLocaleString()}</span>
                    </div>
                    {renderBreakdownFields(singleCosts.external_breakdown, 'external_breakdown')}
                  </div>
                  
                  {/* Internal Costs */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="font-medium">Internal Costs</Label>
                      <span className="text-sm font-semibold">€{internalTotal.toLocaleString()}</span>
                    </div>
                    {renderBreakdownFields(singleCosts.internal_breakdown, 'internal_breakdown')}
                  </div>
                </>
              );
            })()}
          </>
        )}

        <div className="pt-2 border-t">
          <div className="flex justify-between items-center">
            <Label>Total {hasMultipleTools ? 'Project' : ''} Costs</Label>
            <p className="text-lg font-bold">€{total.toLocaleString()}</p>
          </div>
        </div>

        <div>
          <Label htmlFor={`${isActual ? 'actual_' : ''}cost_notes`}>Notes</Label>
          {isEditingInline ? (
            <Textarea
              id={`${isActual ? 'actual_' : ''}cost_notes`}
              value={costs.notes || ""}
              onChange={(e) => setCosts({
                ...costs,
                notes: e.target.value
              })}
              placeholder={`Add notes about ${isActual ? 'actual' : ''} costs...`}
              rows={3}
            />
          ) : (
            <p className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap">
              {costs.notes || "No notes added"}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function ProjectCostTab({ project, isEditingInline, onEditingChange }: ProjectCostTabProps) {
  const [tools, setTools] = useState<string[]>([]);
  const hasMultipleTools = tools.length > 1;
  
  // Track if we're currently saving to prevent useEffect resets
  const [isSaving, setIsSaving] = useState(false);

  // Fetch products for tool names
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id, name, base_price")
        .order("name");
      
      if (error) {
        console.error("Error fetching products:", error);
        throw error;
      }
      return data || [];
    }
  });

  const [soldFor, setSoldFor] = useState(0);

  // Update state when project changes
  useEffect(() => {
    console.log("DEBUG Tools: Project changed", { 
      project: project?.name, 
      tools: project?.tools, 
      toolsType: typeof project?.tools,
      toolsLength: project?.tools?.length 
    });
    
    if (project) {
      // Handle tools
      let projectTools = project.tools || [];
      console.log("DEBUG Tools: Setting tools", { projectTools, type: typeof projectTools });
      setTools(projectTools);
      setSoldFor(parseFloat(project.sold_for) || parseFloat(project.value) || 0);
    }
  }, [project, products]);
  
  
  const [toolPrices, setToolPrices] = useState<{ [toolId: string]: { basePrice: number; soldPrice: number } }>({});

  // Update toolPrices when tools or products change
  useEffect(() => {
    if (tools.length === 0) {
      setToolPrices({});
      return;
    }

    const newToolPrices: { [toolId: string]: { basePrice: number; soldPrice: number } } = {};
    const projectSoldFor = parseFloat(project?.sold_for) || parseFloat(project?.value) || 0;
    
    tools.forEach((toolId: string) => {
      const product = products.find(p => p.id === toolId);
      const basePrice = product?.base_price || 0;
      
      // Try to get sold price from existing project tool_prices data
      let soldPrice = project?.tool_prices?.[toolId]?.soldPrice;
      
      // If no existing sold price but project has a sold_for/value, use that
      if (!soldPrice && projectSoldFor > 0) {
        if (tools.length === 1) {
          // If only one tool, use the full project value
          soldPrice = projectSoldFor;
        } else {
          // If multiple tools, distribute evenly or use base price
          soldPrice = basePrice;
        }
      } else if (!soldPrice) {
        // Default to base price
        soldPrice = basePrice;
      }
      
      newToolPrices[toolId] = {
        basePrice,
        soldPrice: soldPrice || 0
      };
    });
    
    setToolPrices(newToolPrices);
  }, [tools, products, project]);

  // Calculate sold for from tool prices on initialization
  useEffect(() => {
    const calculatedTotal = Object.values(toolPrices).reduce((total, tool) => total + tool.soldPrice, 0);
    if (calculatedTotal > 0 && tools.length > 0) {
      setSoldFor(calculatedTotal);
    }
  }, [toolPrices, tools]);

  const [costEstimation, setCostEstimation] = useState<MultiToolCosts | SingleToolCosts>({
    external_costs: 0,
    internal_costs: 0,
    external_breakdown: getDefaultBreakdown(),
    internal_breakdown: getDefaultBreakdown(),
    notes: ""
  });

  // Update cost estimation when project or tools change
  useEffect(() => {
    if (!project || isSaving) return;
    
    const existing = project.initial_cost_estimation || {};
    
    if (hasMultipleTools) {
      const toolCosts: { [tool: string]: ToolCosts } = {};
      tools.forEach((tool: string) => {
        toolCosts[tool] = {
          external_costs: existing.toolCosts?.[tool]?.external_costs || 0,
          internal_costs: existing.toolCosts?.[tool]?.internal_costs || 0,
          external_breakdown: existing.toolCosts?.[tool]?.external_breakdown || getDefaultBreakdown(),
          internal_breakdown: existing.toolCosts?.[tool]?.internal_breakdown || getDefaultBreakdown(),
        };
      });
      
      setCostEstimation({
        toolCosts,
        general_external_costs: existing.general_external_costs || 0,
        general_internal_costs: existing.general_internal_costs || 0,
        notes: existing.notes || ""
      } as MultiToolCosts);
    } else {
      setCostEstimation({
        external_costs: existing.external_costs || 0,
        internal_costs: existing.internal_costs || 0,
        external_breakdown: existing.external_breakdown || getDefaultBreakdown(),
        internal_breakdown: existing.internal_breakdown || getDefaultBreakdown(),
        notes: existing.notes || ""
      } as SingleToolCosts);
    }
  }, [project, hasMultipleTools, tools, isSaving]);

  const [actualCosts, setActualCosts] = useState<MultiToolCosts | SingleToolCosts>({
    external_costs: 0,
    internal_costs: 0,
    external_breakdown: getDefaultBreakdown(),
    internal_breakdown: getDefaultBreakdown(),
    notes: ""
  });

  // Update actual costs when project or tools change
  useEffect(() => {
    if (!project || isSaving) return;
    
    const existing = project.actual_costs || {};
    
    if (hasMultipleTools) {
      const toolCosts: { [tool: string]: ToolCosts } = {};
      tools.forEach((tool: string) => {
        toolCosts[tool] = {
          external_costs: existing.toolCosts?.[tool]?.external_costs || 0,
          internal_costs: existing.toolCosts?.[tool]?.internal_costs || 0,
          external_breakdown: existing.toolCosts?.[tool]?.external_breakdown || getDefaultBreakdown(),
          internal_breakdown: existing.toolCosts?.[tool]?.internal_breakdown || getDefaultBreakdown(),
        };
      });
      
      setActualCosts({
        toolCosts,
        general_external_costs: existing.general_external_costs || 0,
        general_internal_costs: existing.general_internal_costs || 0,
        notes: existing.notes || ""
      } as MultiToolCosts);
    } else {
      setActualCosts({
        external_costs: existing.external_costs || 0,
        internal_costs: existing.internal_costs || 0,
        external_breakdown: existing.external_breakdown || getDefaultBreakdown(),
        internal_breakdown: existing.internal_breakdown || getDefaultBreakdown(),
        notes: existing.notes || ""
      } as SingleToolCosts);
    }
  }, [project, hasMultipleTools, tools, isSaving]);

  const [priceComments, setPriceComments] = useState("");
  const [paymentSchedule, setPaymentSchedule] = useState({ type: 'once_off' });
  const [extraValuePaymentSchedule, setExtraValuePaymentSchedule] = useState<any>(null);

  // Check if there's an extra value for this project
  const hasExtraValue = parseFloat(project?.extra_value) > 0;

  // Fetch client details to get payment terms
  const { data: clientDetails } = useQuery({
    queryKey: ["client-details", project?.client_id],
    queryFn: async () => {
      if (!project?.client_id) return null;
      const { data, error } = await supabase
        .from("clients")
        .select("payment_terms")
        .eq("id", project.client_id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!project?.client_id,
  });

  // Update other project-dependent states when project changes
  useEffect(() => {
    if (project) {
      setPriceComments(project.price_comments || "");
      
      // Always use the project's payment_schedule if it exists, otherwise default
      const projectPaymentSchedule = project.payment_schedule || { type: 'once_off' };
      
      // Convert client payment_terms to payment schedule format
      let clientPaymentTerms = '60_days'; // default
      if (clientDetails?.payment_terms) {
        const clientTerms = clientDetails.payment_terms.toLowerCase().replace(' ', '_');
        switch (clientTerms) {
          case 'immediately':
            clientPaymentTerms = 'immediately';
            break;
          case '30_days':
            clientPaymentTerms = '30_days';
            break;
          case '90_days':
            clientPaymentTerms = '90_days';
            break;
          case '120_days':
            clientPaymentTerms = '120_days';
            break;
          default:
            clientPaymentTerms = '60_days';
        }
      }
      
      // Only set client payment terms if no payment_terms exist and it's a once_off type
      if (!projectPaymentSchedule.payment_terms && projectPaymentSchedule.type === 'once_off' && clientDetails?.payment_terms) {
        projectPaymentSchedule.payment_terms = clientPaymentTerms;
      }
      
      console.log('Setting payment schedule in ProjectCostTab:', { 
        projectId: project.id, 
        projectName: project.name,
        fromProject: project.payment_schedule, 
        setting: projectPaymentSchedule 
      });
      
      setPaymentSchedule(projectPaymentSchedule);
      
      // Set extra value payment schedule if exists
      if (project.extra_value_payment_schedule) {
        setExtraValuePaymentSchedule(project.extra_value_payment_schedule);
      } else if (parseFloat(project.extra_value) > 0) {
        // Default to once_off for extra value if not set
        setExtraValuePaymentSchedule({ type: 'once_off' });
      } else {
        setExtraValuePaymentSchedule(null);
      }
    }
  }, [project?.id, project?.payment_schedule, project?.extra_value_payment_schedule, project?.extra_value, clientDetails]);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Update tool prices when products data changes
  useEffect(() => {
    if (products.length > 0) {
      const updatedPrices = { ...toolPrices };
      let hasChanges = false;
      
      tools.forEach((toolId: string) => {
        if (!updatedPrices[toolId]) {
          const product = products.find(p => p.id === toolId);
          const basePrice = product?.base_price || 0;
          const existingSoldPrice = project.tool_prices?.[toolId]?.soldPrice || basePrice;
          
          updatedPrices[toolId] = {
            basePrice,
            soldPrice: existingSoldPrice
          };
          hasChanges = true;
        }
      });
      
      // Remove prices for tools that are no longer selected
      Object.keys(updatedPrices).forEach(toolId => {
        if (!tools.includes(toolId)) {
          delete updatedPrices[toolId];
          hasChanges = true;
        }
      });
      
      if (hasChanges) {
        setToolPrices(updatedPrices);
        // Update soldFor when tool prices are initialized
        const newTotal = Object.values(updatedPrices).reduce((total, tool) => total + tool.soldPrice, 0);
        setSoldFor(newTotal);
      }
    }
  }, [products, tools, project.tool_prices]);

  // Update cost estimations when tools change
  useEffect(() => {
    const updateCosts = (currentCosts: MultiToolCosts | SingleToolCosts): MultiToolCosts | SingleToolCosts => {
      if (hasMultipleTools) {
        const toolCosts: { [tool: string]: ToolCosts } = {};
        tools.forEach((tool: string) => {
          // Keep existing costs if available, otherwise initialize
          if ('toolCosts' in currentCosts && currentCosts.toolCosts[tool]) {
            toolCosts[tool] = currentCosts.toolCosts[tool];
          } else {
            toolCosts[tool] = { 
              external_costs: 0, 
              internal_costs: 0,
              external_breakdown: getDefaultBreakdown(),
              internal_breakdown: getDefaultBreakdown()
            };
          }
        });
        
        return {
          toolCosts,
          general_external_costs: ('general_external_costs' in currentCosts) ? currentCosts.general_external_costs : 0,
          general_internal_costs: ('general_internal_costs' in currentCosts) ? currentCosts.general_internal_costs : 0,
          notes: currentCosts.notes || ""
        } as MultiToolCosts;
      } else {
        return {
          external_costs: ('external_costs' in currentCosts) ? currentCosts.external_costs : 0,
          internal_costs: ('internal_costs' in currentCosts) ? currentCosts.internal_costs : 0,
          external_breakdown: ('external_breakdown' in currentCosts) ? currentCosts.external_breakdown : getDefaultBreakdown(),
          internal_breakdown: ('internal_breakdown' in currentCosts) ? currentCosts.internal_breakdown : getDefaultBreakdown(),
          notes: currentCosts.notes || ""
        } as SingleToolCosts;
      }
    };

    setCostEstimation(current => updateCosts(current));
    setActualCosts(current => updateCosts(current));
  }, [tools, hasMultipleTools]);

  // Calculate total sold price from individual tool prices
  const calculateTotalSoldPrice = () => {
    return Object.values(toolPrices).reduce((total, tool) => total + tool.soldPrice, 0);
  };

  // Update soldFor when tool prices change
  const updateToolPrice = (toolId: string, soldPrice: number) => {
    const updated = {
      ...toolPrices,
      [toolId]: {
        ...toolPrices[toolId],
        soldPrice
      }
    };
    setToolPrices(updated);
    
    // Update total sold for
    const newTotal = Object.values(updated).reduce((total, tool) => total + tool.soldPrice, 0);
    setSoldFor(newTotal);
  };

  const updateCostMutation = useMutation({
    mutationFn: async (params: { 
      estimation: MultiToolCosts | SingleToolCosts; 
      actual: MultiToolCosts | SingleToolCosts;
      soldFor: number;
      toolPrices: { [toolId: string]: { basePrice: number; soldPrice: number } };
      priceComments: string;
      paymentSchedule: any;
      extraValuePaymentSchedule: any;
      tools: string[];
    }) => {
      setIsSaving(true);
      const { data, error } = await supabase
        .from("projects")
        .update({
          initial_cost_estimation: params.estimation as any,
          actual_costs: params.actual as any,
          sold_for: params.soldFor,
          tool_prices: params.toolPrices,
          price_comments: params.priceComments,
          payment_schedule: params.paymentSchedule,
          extra_value_payment_schedule: params.extraValuePaymentSchedule,
          tools: params.tools,
        })
        .eq("id", project.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      onEditingChange(false);
      toast({
        title: "Success",
        description: "Cost data updated successfully",
      });
      // Reset isSaving after a delay to allow the query to refetch
      setTimeout(() => setIsSaving(false), 500);
    },
    onError: (error: any) => {
      setIsSaving(false);
      toast({
        title: "Error",
        description: "Failed to update cost data: " + error.message,
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    updateCostMutation.mutate({ 
      estimation: costEstimation, 
      actual: actualCosts,
      soldFor: soldFor,
      toolPrices: toolPrices,
      priceComments: priceComments,
      paymentSchedule: paymentSchedule,
      extraValuePaymentSchedule: extraValuePaymentSchedule,
      tools: tools
    });
  };

  const handleCancel = () => {
    // Reset to original values
    setTools(project.tools || []);
    setSoldFor(parseFloat(project.sold_for) || 0);
    setPriceComments(project.price_comments || "");
    setPaymentSchedule(project.payment_schedule || { type: 'once_off' });
    
    // Reset tool prices
    const resetPrices: { [toolId: string]: { basePrice: number; soldPrice: number } } = {};
    tools.forEach((toolId: string) => {
      const product = products.find(p => p.id === toolId);
      const basePrice = product?.base_price || 0;
      const existingSoldPrice = project.tool_prices?.[toolId]?.soldPrice || basePrice;
      
      resetPrices[toolId] = {
        basePrice,
        soldPrice: existingSoldPrice
      };
    });
    setToolPrices(resetPrices);
    
    // Reset sold for to calculated total
    const calculatedTotal = Object.values(resetPrices).reduce((total, tool) => total + tool.soldPrice, 0);
    setSoldFor(calculatedTotal);
    
    const existing = project.initial_cost_estimation || {};
    const existingActual = project.actual_costs || {};
    
    if (hasMultipleTools) {
      const toolCosts: { [tool: string]: ToolCosts } = {};
      const actualToolCosts: { [tool: string]: ToolCosts } = {};
      tools.forEach((tool: string) => {
        toolCosts[tool] = {
          external_costs: existing.toolCosts?.[tool]?.external_costs || 0,
          internal_costs: existing.toolCosts?.[tool]?.internal_costs || 0,
          external_breakdown: existing.toolCosts?.[tool]?.external_breakdown || getDefaultBreakdown(),
          internal_breakdown: existing.toolCosts?.[tool]?.internal_breakdown || getDefaultBreakdown(),
        };
        actualToolCosts[tool] = {
          external_costs: existingActual.toolCosts?.[tool]?.external_costs || 0,
          internal_costs: existingActual.toolCosts?.[tool]?.internal_costs || 0,
          external_breakdown: existingActual.toolCosts?.[tool]?.external_breakdown || getDefaultBreakdown(),
          internal_breakdown: existingActual.toolCosts?.[tool]?.internal_breakdown || getDefaultBreakdown(),
        };
      });
      
      setCostEstimation({
        toolCosts,
        general_external_costs: existing.general_external_costs || 0,
        general_internal_costs: existing.general_internal_costs || 0,
        notes: existing.notes || ""
      } as MultiToolCosts);
      
      setActualCosts({
        toolCosts: actualToolCosts,
        general_external_costs: existingActual.general_external_costs || 0,
        general_internal_costs: existingActual.general_internal_costs || 0,
        notes: existingActual.notes || ""
      } as MultiToolCosts);
    } else {
      setCostEstimation({
        external_costs: existing.external_costs || existing.external_partners || 0,
        internal_costs: existing.internal_costs || existing.internal_hours || 0,
        external_breakdown: existing.external_breakdown || getDefaultBreakdown(),
        internal_breakdown: existing.internal_breakdown || getDefaultBreakdown(),
        notes: existing.notes || ""
      } as SingleToolCosts);
      
      setActualCosts({
        external_costs: existingActual.external_costs || 0,
        internal_costs: existingActual.internal_costs || 0,
        external_breakdown: existingActual.external_breakdown || getDefaultBreakdown(),
        internal_breakdown: existingActual.internal_breakdown || getDefaultBreakdown(),
        notes: existingActual.notes || ""
      } as SingleToolCosts);
    }
    onEditingChange(false);
  };

  // Calculate totals
  const calculateTotal = (costs: MultiToolCosts | SingleToolCosts): number => {
    if (hasMultipleTools && 'toolCosts' in costs) {
      const toolsTotal = Object.values(costs.toolCosts).reduce((sum, tool) => {
        const externalTotal = calculateBreakdownTotal(tool.external_breakdown);
        const internalTotal = calculateBreakdownTotal(tool.internal_breakdown);
        return sum + externalTotal + internalTotal;
      }, 0);
      return toolsTotal;
    } else if (!hasMultipleTools && 'external_breakdown' in costs) {
      const externalTotal = calculateBreakdownTotal(costs.external_breakdown);
      const internalTotal = calculateBreakdownTotal(costs.internal_breakdown);
      return externalTotal + internalTotal;
    }
    return 0;
  };

  const initialTotal = calculateTotal(costEstimation);
  const actualTotal = calculateTotal(actualCosts);
  const profitability = soldFor > 0 ? ((soldFor - actualTotal) / soldFor * 100) : 0;
  const profitAmount = soldFor - actualTotal;

  return (
    <div className="space-y-6">
      {isEditingInline && (
        <div className="flex items-center justify-end space-x-2">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateCostMutation.isPending}>
            {updateCostMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      )}


      {/* Sold For Field */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <DollarSign className="h-5 w-5 mr-2" />
            Profitability
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label>Sold For (Revenue)</Label>
              <p className="mt-1 text-lg font-semibold">€{soldFor.toLocaleString()}</p>
            </div>

            <div>
              <Label>Cost</Label>
              <p className="mt-1 text-lg font-semibold">€{actualTotal.toLocaleString()}</p>
            </div>

            <div>
              <Label>Profitability</Label>
              <div className="flex items-center space-x-2 mt-1">
                <p className="text-lg font-bold">€{profitAmount.toLocaleString()}</p>
                <Badge 
                  variant={profitability > 0 ? "default" : "destructive"}
                  className={profitability > 0 ? "bg-green-100 text-green-800" : ""}
                >
                  {profitability.toFixed(1)}%
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Price Component */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Euro className="h-5 w-5 mr-2" />
            Price
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tools Selection within Price */}
          <div className="pb-4 border-b">
            <Label htmlFor="tools">Select Tools</Label>
            {isEditingInline ? (
              <MultiSelectProducts 
                products={products}
                value={tools}
                onChange={setTools}
                placeholder="Select tools"
              />
            ) : (
              <p className="text-sm mt-2">
                {tools && tools.length > 0 ? (
                  <span className="font-medium">
                    {tools.map(toolId => 
                      products.find(p => p.id === toolId)?.name
                    ).filter(Boolean).join(", ")}
                  </span>
                ) : (
                  <span className="text-muted-foreground">No tools selected</span>
                )}
              </p>
            )}
          </div>
          {tools.length > 0 ? (
            <>
              {tools.map((toolId: string) => {
                const product = products.find(p => p.id === toolId);
                const toolPrice = toolPrices[toolId] || { basePrice: 0, soldPrice: 0 };
                
                return (
                  <div key={toolId} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{product?.name || "Unknown Product"}</p>
                      {!product && (
                        <p className="text-xs text-muted-foreground">Product not found</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-sm">
                        <span className="text-muted-foreground">List price: </span>
                        <span className="font-medium">€{toolPrice.basePrice.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-muted-foreground">Sold:</span>
                        {isEditingInline ? (
                          <Input
                            type="text"
                            step="0.01"
                            value={toolPrice.soldPrice === 0 ? '' : toolPrice.soldPrice.toString().replace('.', ',')}
                            onChange={(e) => {
                              const value = e.target.value.replace(',', '.');
                              const numericValue = parseFloat(value);
                              if (!isNaN(numericValue) || value === '') {
                                updateToolPrice(toolId, numericValue || 0);
                              }
                            }}
                            placeholder="0"
                            className="w-24"
                          />
                        ) : (
                          <span className="font-medium">€{toolPrice.soldPrice.toString().replace('.', ',')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="pt-2 border-t space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">List Price Total:</span>
                  <span className="text-lg font-bold">€{Object.values(toolPrices).reduce((sum, tool) => sum + tool.basePrice, 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium">Sold for:</span>
                  <span className="text-lg font-bold">€{calculateTotalSoldPrice().toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium">vs List price:</span>
                  <span className={`text-lg font-bold ${
                    calculateTotalSoldPrice() >= Object.values(toolPrices).reduce((sum, tool) => sum + tool.basePrice, 0) 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    {Object.values(toolPrices).reduce((sum, tool) => sum + tool.basePrice, 0) > 0 
                      ? ((calculateTotalSoldPrice() - Object.values(toolPrices).reduce((sum, tool) => sum + tool.basePrice, 0)) / Object.values(toolPrices).reduce((sum, tool) => sum + tool.basePrice, 0) * 100).toFixed(1)
                      : '0.0'
                    }%
                  </span>
                </div>
              </div>
              
              {/* Comments Field */}
              <div className="pt-4 border-t">
                <Label htmlFor="price_comments">Comments</Label>
                {isEditingInline ? (
                  <Textarea
                    id="price_comments"
                    value={priceComments}
                    onChange={(e) => setPriceComments(e.target.value)}
                    placeholder="Add comments about pricing..."
                    rows={3}
                    className="mt-2"
                  />
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap">
                    {priceComments || "No comments added"}
                  </p>
                )}
              </div>
            </>
          ) : (
            <p className="text-muted-foreground text-center py-4">
              No tools assigned to this project
            </p>
          )}
        </CardContent>
      </Card>

      {/* Payment Schedules - Side by side layout */}
      <div className={`grid gap-6 ${hasExtraValue ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
        {/* Initial Value Payment Schedule */}
        <PaymentComponent 
          project={project} 
          isEditingInline={isEditingInline}
          paymentSchedule={paymentSchedule}
          onPaymentScheduleChange={setPaymentSchedule}
          title="Invoicing Schedule (Initial Value)"
          projectValue={parseFloat(project.sold_for) || 0}
        />

        {/* Extra Value Payment Schedule - Only shown when extra_value exists */}
        {hasExtraValue && (
          <PaymentComponent 
            project={project} 
            isEditingInline={isEditingInline}
            paymentSchedule={extraValuePaymentSchedule || { type: 'once_off' }}
            onPaymentScheduleChange={setExtraValuePaymentSchedule}
            title="Invoicing Schedule (Extra Value)"
            projectValue={parseFloat(project.extra_value) || 0}
          />
        )}
      </div>

      {/* Cost Components */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Cost Estimation */}
        <CostComponent
          title="Cost Estimation"
          icon={<Calculator className="h-5 w-5 mr-2" />}
          costs={costEstimation}
          setCosts={setCostEstimation}
          tools={tools}
          products={products}
          hasMultipleTools={hasMultipleTools}
          isEditingInline={isEditingInline}
          isActual={false}
        />

        {/* Actual Cost */}
        <CostComponent
          title="Actual Cost"
          icon={<TrendingUp className="h-5 w-5 mr-2" />}
          costs={actualCosts}
          setCosts={setActualCosts}
          tools={tools}
          products={products}
          hasMultipleTools={hasMultipleTools}
          isEditingInline={isEditingInline}
          isActual={true}
        />
      </div>
    </div>
  );
}